import React, { Fragment, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Link as MuiLink, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { login, logout, addMfaDevice, validateUserRole } from "../../action/auth";
import { getTenantInfo } from "../../action/tenant";
import Alert from "../layout/Alert";
import UseForm from "./UseForm";
import validate from "./LoginFormValidationRules";
import ReCAPTCHA from "react-google-recaptcha";
import customConfig from "../../config";
import env from "../../env";
import MfaQRcode from "./mfaQRcode";
import MfaValidate from "./mfaValidationCode";
import ForgotPassword from "./forgotPassword";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";


// const CLIENT_ID = env.GET_CLIENT_ID();
const SCOPE = process.env.REACT_APP_SCOPE;

const Login = ({
  login,
  isAuthenticated,
  logout,
  getTenantInfo,
  addMfaDevice,
  qrCodeUrl,
  mfaToken,
  validateUserRole,
}) => {
  const captchaRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const theme = useTheme();
  const [recaptchaValue, setRecaptchaValue] = useState();
  const [showError, setShowError] = useState(false);
  const { handleSubmit, InputOnFocus, handleChange, values, errors } = UseForm(
    loginForm,
    validate,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [tenantData, setTenantData] = useState({});
  const [tmsAPIError, setTmsAPIError] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [mfavalidationCode, setMfaValidationCode] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaRenderKey, setCaptchaRenderKey] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(true);
  // Measure the rendered login column width so we can scale the fixed-width reCAPTCHA
  // widget to match MUI inputs on very narrow viewports (e.g. foldable phones).
  const formStackRef = useRef(null);
  const [captchaScale, setCaptchaScale] = useState(1);
  // Debounce ResizeObserver callbacks into a single animation frame to avoid thrashing
  // React state while Google reCAPTCHA is mounting (can surface as Timeout errors).
  const captchaScaleRafRef = useRef(null);
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down("tablet"));
  const isMobileXsOrBelow = useMediaQuery(theme.breakpoints.down("mobileXs"));
  const loginLayoutKey = isMobileXsOrBelow
    ? "mobile-xs"
    : isTabletOrBelow
    ? "tablet"
    : "desktop";
  // Keep the form column width aligned with the native checkbox widget width (~304px).
  const formContentMaxWidth = 304;

  // Google checkbox reCAPTCHA renders at a fixed intrinsic width; we treat that as the
  // baseline and scale down only when the actual column is narrower than this.
  const CAPTCHA_BASE_WIDTH = 304;

  const updateCaptchaScale = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (captchaScaleRafRef.current != null) {
      window.cancelAnimationFrame(captchaScaleRafRef.current);
    }

    captchaScaleRafRef.current = window.requestAnimationFrame(() => {
      captchaScaleRafRef.current = null;

      const el = formStackRef.current;
      if (!el) {
        return;
      }

      const w = el.getBoundingClientRect().width;
      if (!w || !Number.isFinite(w)) {
        return;
      }

      const next = Math.min(1, w / CAPTCHA_BASE_WIDTH);
      setCaptchaScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    });
  }, []);

  useLayoutEffect(() => {
    // Recompute after anything that can change the form column geometry.
    updateCaptchaScale();
  }, [updateCaptchaScale, loginLayoutKey, captchaRenderKey, showCaptcha]);

  // Keep captchaScale in sync with real layout changes (devtools responsive mode, font
  // scaling, orientation changes) without spamming updates on every sub-pixel fluctuation.
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCaptchaScale);
      return () => window.removeEventListener("resize", updateCaptchaScale);
    }

    const el = formStackRef.current;
    if (!el) {
      return undefined;
    }

    const ro = new ResizeObserver(() => updateCaptchaScale());
    ro.observe(el);
    window.addEventListener("resize", updateCaptchaScale);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateCaptchaScale);
      if (captchaScaleRafRef.current != null) {
        window.cancelAnimationFrame(captchaScaleRafRef.current);
        captchaScaleRafRef.current = null;
      }
    };
  }, [updateCaptchaScale]);

  const handleOpenForgotPassword = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setForgotOpen(true);
  };

  const rerenderCaptcha = (delay = 120) => {
    if (typeof window === "undefined") {
      return;
    }

    // Fully remount the widget (new key) after a tick. Avoid manually removing Google's
    // iframes from the document; that can interrupt script load and throw Timeout errors.
    setShowCaptcha(false);
    window.setTimeout(() => {
      setCaptchaRenderKey((prev) => prev + 1);
      setShowCaptcha(true);
    }, delay);
  };

  useEffect(() => {
    setIsLoading(true);
    getTenantInfo()
      .then((res) => {
        setIsLoading(false);
        setTenantData(res);
        localStorage.setItem("tenantInfo", JSON.stringify(res));
      })
      .catch((err) => {
        setIsLoading(false);
        setTmsAPIError(true);
      });
  }, [getTenantInfo]);

  useEffect(() => {
    if (qrCodeUrl) {
      setShowQrCode(true);
    }
    if (mfaToken) {
      setMfaValidationCode(true);
    }
  }, [qrCodeUrl, mfaToken]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const prevOverflow = document.body.style.overflow;
    const prevOverflowY = document.body.style.overflowY;
    const prevHeight = document.body.style.height;

    // Login page needs body scrolling on small/touch screens so the reCAPTCHA
    // challenge frame can be fully reachable.
    document.body.style.overflow = "auto";
    document.body.style.overflowY = "auto";
    document.body.style.height = "auto";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.overflowY = prevOverflowY;
      document.body.style.height = prevHeight;
    };
  }, []);

  useEffect(() => {
    let resetTimer;

    // When the responsive "bucket" changes (desktop/tablet/mobile), remount captcha so
    // devtools device emulation does not leave a stale iframe/challenge state behind.
    setRecaptchaValue("");
    setShowError(false);
    captchaRef.current?.reset?.();
    setShowCaptcha(false);

    resetTimer = window.setTimeout(() => {
      setCaptchaRenderKey((prev) => prev + 1);
      setShowCaptcha(true);
    }, 180);

    return () => {
      window.clearTimeout(resetTimer);
    };
  }, [loginLayoutKey]);

  async function loginForm() {
    if (!recaptchaValue) {
      setShowError(true);
      return;
    }

    try {
      const res = await login(
        values.mobile,
        values.password,
        env.GET_CLIENT_ID(),
        SCOPE,
        recaptchaValue,
      );
      if (res?.data?.accessToken) {
        await validateUserRole(res);
        return; // do not reset
      }
    } catch (err) {
    } finally {
      if (isMountedRef.current && captchaRef.current) {
        setRecaptchaValue("");
        captchaRef.current?.reset();
      }
      // resetCaptcha(); // reset only after login finishes
    }
  }
  // const resetCaptcha = () => {
  //   captchaRef.current?.reset();
  //   setRecaptchaValue("");
  // };

  const onChangeRecaptcha = (value) => {
    setRecaptchaValue(value);
    setShowError(false);
  };

  const errorCallback = () => {
    setRecaptchaValue("");
    setShowError(true);
    captchaRef.current?.reset?.();
    // Recreate widget on Google-side errors instead of relying on broken internal UI.
    rerenderCaptcha();
  };

  const onExpiredCallback = () => {
    setRecaptchaValue("");
    setShowError(true);
    captchaRef.current?.reset?.();
    // Same as error: expired tokens should not leave the widget in a half-broken state.
    rerenderCaptcha();
  };

  if (isAuthenticated) {
    // if(mfaToken === null){
    //   alert(customConfig.mfaEnableMsg);
    // }
    
    return <Navigate to="/landing" />;
  }

  if (showQrCode && qrCodeUrl) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <MfaQRcode tenantData={tenantData} />
      </div>
    );
  }

  if (mfaToken && mfavalidationCode) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <MfaValidate tenantData={tenantData} />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="login-wrapper login-screen">
        <div className="login-box">
          <div
            style={{
              display: "grid",
              placeItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size="5em" style={{ color: "#AFB42B" }} />
          </div>
        </div>
      </div>
    );
  }
  if (tmsAPIError) {
    return (
      <div
        style={{
          background: "#f0f0f0",
          display: "grid",
          placeItems: "center",
          width: "100%",
          textAlign: "center",
          paddingTop: "10vh",
        }}
      >
        <div
          style={{
            width: "80%",
            height: "80vh",
            display: "grid",
            placeItems: "center",
            background: "#fcfcfc",
          }}
        >
          <div style={{ paddingBottom: 0 }}>
            <p
              style={{
                fontWeight: "bold",
                color: "#B8B8B8",
              }}
            >
              <span style={{ fontSize: 100, color: "grey" }}>500 </span>
              <span style={{ fontSize: 60, color: "#bd2d19" }}>
                Server Error
              </span>
            </p>
          </div>
          <div
            style={{
              fontSize: 25,
              textAlign: "left",
            }}
          >
            <p>Oops, something went wrong.</p>
            <br></br>
            <p>
              Try to refresh this page or feel free to contact us if the problem
              persists
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <Fragment>
      <div className="login-wrapper login-screen">
        <div className="login-box">
          <div className="form-box">
            <div className="row mb-0">
              <Alert />
              <div className="col s12 center-align">
                {tenantData?.tenantLogoUrl && (
                  <img
                    src={tenantData.tenantLogoUrl}
                    alt="Logo"
                    className="logo-outer"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
            </div>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
              <Stack
                ref={formStackRef}
                spacing={2.25}
                sx={{
                  width: "100%",
                  // This column width is what we measure for captchaScale (see updateCaptchaScale).
                  maxWidth: formContentMaxWidth,
                  mx: "auto",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                    User Name
                  </Typography>
                  <FormInput
                    type="text"
                    name="mobile"
                    autoComplete="username"
                    value={values.mobile || ""}
                    onChange={handleChange}
                    onInput={InputOnFocus}
                    required
                    placeholder="Enter User Name"
                    error={Boolean(errors.mobile)}
                    helperText={errors.mobile || ""}
                    touched="true"
                    fullWidth
                  />
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                    Password
                  </Typography>
                  <FormInput
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    value={values.password || ""}
                    onChange={handleChange}
                    onInput={InputOnFocus}
                    minLength="6"
                    placeholder="Enter Password"
                    required
                    error={Boolean(errors.password)}
                    helperText={errors.password || ""}
                    touched="true"
                    fullWidth
                    endAdornment={
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    }
                  />
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
                  Forgot Password? Click to{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleOpenForgotPassword}
                    underline="always"
                    sx={{
                      color: "primary.main",
                      fontSize: 12,
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      border: 0,
                      padding: 0,
                      "&:focus": { outline: "none", boxShadow: "none", backgroundColor: "transparent" },
                      "&:focus-visible": { outline: "none", boxShadow: "none", backgroundColor: "transparent" },
                    }}
                  >
                    reset
                  </MuiLink>
                  .
                </Typography>

                <Box
                  className="capcha-box"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mt: 0.5,
                    // Reserve a little vertical room when our own validation message is shown
                    // under the widget (separate from Google's internal iframe text).
                    minHeight: showError
                      ? {
                          xs: 110,
                          sm: 104,
                        }
                      : "auto",
                  }}
                >
                  {showCaptcha && (
                    <Box
                      sx={{
                        width: CAPTCHA_BASE_WIDTH,
                        maxWidth: "100%",
                        transform: `scale(${captchaScale})`,
                        transformOrigin: "top center",
                      }}
                    >
                      <ReCAPTCHA
                        key={`${loginLayoutKey}-${captchaRenderKey}`}
                        ref={captchaRef}
                        sitekey={process.env.REACT_APP_CAPTCHA_API_KEY}
                        onChange={onChangeRecaptcha}
                        onErrored={errorCallback}
                        onExpired={onExpiredCallback}
                      />
                    </Box>
                  )}
                  {showError && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{
                        mt: 1.25,
                        minHeight: 36,
                        lineHeight: 1.25,
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {customConfig.captchaErrorMsg}
                    </Typography>
                  )}
                </Box>

                <AppButton
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  sx={{ mt: 0.5, fontSize: 17, fontWeight: 600 }}
                >
                  Sign In
                </AppButton>
              </Stack>
            </Box>
          </div>

          {/* <div className="white-text center font-13">
            Copyright © 2020 Panasonic India Pvt Ltd.
          </div> */}
        </div>
        {/* <div className="footer login font-13  font-300i">
          Designed and Developed by Panasonic India Innovation Center
        </div>{" "} */}
        <ForgotPassword
          open={forgotOpen}
          onClose={() => setForgotOpen(false)}
        />
      </div>
    </Fragment>
  );
};
Login.propTypes = {
  login: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  qrCodeUrl: PropTypes.string,
  addMfaDevice: PropTypes.func.isRequired,
  mfaToken: PropTypes.string,
  validateUserRole: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  qrCodeUrl: state.auth.qrCodeUrl,
  mfaToken: state.auth.mfaToken,
});

const mapDispatchToProps = (dispatch) => {
  return {
    login: login(dispatch),
    logout: logout(dispatch),
    getTenantInfo: getTenantInfo(dispatch),
    addMfaDevice: (accessToken) => dispatch(addMfaDevice(accessToken)),
    validateUserRole: (res) => dispatch(validateUserRole(res)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(Login);

